import { FC, Fragment, ReactNode, useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import * as jwt from 'jsonwebtoken';
import { useLazyQuery, useQuery } from '@apollo/client';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { BreadcrumbsArray } from '../Breadcrumbs';
import { GlobalAppContext } from '../ContextProvider';
import Header from '../Header';
import MobileSideBar from './MobileSideBar';
import WebSideBar from './WebSideBar';
import { getPortalOrganisation, isMobile, isTablet, writePortalOrganisation } from '../../../utils';
import DeclarationModal from '../../../containers/shared/declaration';
import { User } from '../../../containers/shared/auth/AuthContext';
import { GET_PORTAL_ORGANISATION, GRANTS_ACCESS } from '../../../graphql';
import { GET_PORTAL_ORGANISATIONType } from '../../../graphql/queries/GetPortalOrganisation';
import { Cookies } from 'react-cookie';
import { JWT_COOKIE_KEY } from '../../../consts';
import { organisationLogo } from './organisationLogo';
import { SCREEN } from '../../../theme/breakpoints';

const isWeb = SCREEN.isWeb;

export type NavItem = {
  to: string;
  title: string;
  icon: ReactNode;
  subMenu?: Array<{
    to: string;
    title: string;
    check?: (user: User, hasCaseManagement: boolean) => boolean;
    locked?: (user: User) => boolean;
  }>;
  hideItemIfModuleChecked?: boolean;
  /** Highlights a single item on this path and any path below it. */
  activePathPrefix?: string;
  check?: (user: User, hasCaseManagement: boolean) => boolean;
};
export type PortalType = 'admin' | 'service-provider' | 'funder' | 'institution';

export type SideBarProps = RouteComponentProps & {
  portalType: PortalType;
  acknowledgedDeclaration: boolean;
  authenticated: boolean;
  breadcrumbs: BreadcrumbsArray;
  color?: string;
  history: History;
  className?: string;
  children?: ReactNode;
  navItems: Array<NavItem>;
  userOrganisationId?: string;
  isMobile?: boolean;
  isTablet?: boolean;
  isWeb?: boolean;
  logout?: Function;
};

type RenderWrapperProps = {
  isWeb?: boolean;
  open?: boolean;
};

const RenderWrapper = styled.div<RenderWrapperProps>`
  width: 100%;
  display: flex;
  min-height: calc(100vh / var(--ib-ui-scale, 1));
  padding-left: ${props => (props.isWeb ? (props.open ? '250px' : '60px') : '0')};
  flex-direction: column;
  align-items: center;
`;

const Sidebar: FC<SideBarProps> = props => {
  const {
    acknowledgedDeclaration,
    authenticated,
    navItems,
    children,
    color,
    breadcrumbs,
    portalType,
    userOrganisationId,
    logout,
  } = props;
  const [isDeclarationModalOpen, setIsDeclarationModalOpen] = useState<boolean>(
    authenticated && !acknowledgedDeclaration
  );
  const [portalOrganisation, setPortalOrganisation] = useState<GET_PORTAL_ORGANISATIONType>(null);

  const [fetchOrganisations, { data: portalOrganisationData, error: portalOrganisationError }] = useLazyQuery(
    GET_PORTAL_ORGANISATION,
    {
      variables: {
        portal: portalType,
      },
    }
  );

  useEffect(() => {
    const getCookies = new Cookies();
    const token = getCookies.get(`${JWT_COOKIE_KEY}`);
    const decodedJWT: any = jwt.decode(token);
    let interval = 10000;
    if (decodedJWT && decodedJWT.exp) {
      const remaining = decodedJWT.exp * 1000 - Date.now();
      if (remaining > 10000) interval = remaining;
    }
    const tokenCheckInterval = setInterval(() => {
      const tokenExpValid = decodedJWT && decodedJWT.exp ? Date.now() / 1000 < decodedJWT.exp : false;

      if (!tokenExpValid) {
        // An expired cached token must not throw a field user out while they are offline.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        clearInterval(tokenCheckInterval);
        if (logout) logout();
      }
    }, interval);
    return () => clearInterval(tokenCheckInterval);
  }, []);

  // IB-1897: Grants is hidden for users with no Grants access (it stays, locked, when only the plan is short)
  const { data: grantsAccessData } = useQuery(GRANTS_ACCESS, {
    skip: !authenticated || portalType !== 'funder' || !userOrganisationId,
    // refreshed on every mount: access differs per organisation and portal, and users switch between them
    fetchPolicy: 'cache-and-network',
  });
  const grantsAccess = grantsAccessData && grantsAccessData.grantsAccess;
  const hideGrants = !!grantsAccess && !grantsAccess.allowed && grantsAccess.reason !== 'PLAN';

  useEffect(() => {
    if (authenticated && !!portalType && !!userOrganisationId) {
      fetchOrganisations({
        variables: {
          portal: portalType,
        },
      });
    }
  }, [authenticated, portalType, userOrganisationId]);

  useEffect(() => {
    if (authenticated && portalOrganisationError && !portalOrganisationData) {
      // handler for when error should fetch portalOrganisation from indexedDB
      getPortalOrganisation().then(data => setPortalOrganisation(data));
    }
  }, [authenticated, portalOrganisationError]);

  useEffect(() => {
    (async () => {
      if (authenticated && !portalOrganisationError && !!portalOrganisationData) {
        await writePortalOrganisation(portalOrganisationData, 'Sidebar.useEffect');
        setPortalOrganisation(portalOrganisationData);
      }
    })();
  }, [authenticated, portalOrganisationData]);

  return (
    <GlobalAppContext.Consumer>
      {context => {
        // Logic to hide the organisation's checked modules.
        let preferredNavItems: any[] = [];
        const organisation = portalOrganisation;
        if (organisation && organisation.organisation && organisation.organisation.modules) {
          navItems.forEach((n: any) => {
            if (n.hideItemIfModuleChecked) {
              if (
                !(
                  organisation &&
                  organisation.organisation &&
                  organisation.organisation.modules.find((m: any) => m.name === n.title)
                )
              ) {
                preferredNavItems.push(n);
              }
            } else if (n.subMenu) {
              const newSubMenu = n.subMenu.filter((s: any) => {
                if (hideGrants && s.to === '/funder/manage/grants') {
                  return false;
                }
                const isFormsMenu = s.title === 'Forms';
                if (isFormsMenu) {
                  return (
                    organisation &&
                    organisation.organisation &&
                    !organisation.organisation.modules.find((m: any) => m.name === 'Manage Forms')
                  );
                } else {
                  return (
                    organisation &&
                    organisation.organisation &&
                    !organisation.organisation.modules.some((m: any) => m.name === s.title || m.name.includes(s.title))
                  );
                }
              });

              if (newSubMenu.length > 0) {
                preferredNavItems.push({
                  ...n,
                  subMenu: newSubMenu,
                });
              }
            } else if (
              organisation &&
              organisation.organisation &&
              !organisation.organisation.modules.find((m: any) => m.name === n.title)
            ) {
              preferredNavItems.push(n);
            }
          });
        } else if (portalType === 'admin') {
          preferredNavItems = navItems;
        }

        if (preferredNavItems) {
          return (
            <Fragment>
              <DeclarationModal
                open={isDeclarationModalOpen}
                openHandler={setIsDeclarationModalOpen}
                onClose={() => null}
              />
              {isWeb ? (
                <WebSideBar
                  portalType={portalType}
                  color={color}
                  context={context}
                  isWeb={isWeb}
                  navItems={preferredNavItems}
                  organisationLogo={organisationLogo(portalOrganisation)}
                />
              ) : (
                <MobileSideBar portalType={portalType} context={context} navItems={preferredNavItems} isWeb={isWeb} />
              )}
              <RenderWrapper open={context.drawerOpen} isWeb={isWeb}>
                <Header
                  breadcrumbs={breadcrumbs}
                  isMobile={isMobile}
                  isTablet={isTablet}
                  isWeb={isWeb}
                  context={context}
                />
                {children}
              </RenderWrapper>
            </Fragment>
          );
        } else {
          return null;
        }
      }}
    </GlobalAppContext.Consumer>
  );
};

Sidebar.defaultProps = {
  navItems: [],
};

export default withRouter(Sidebar);
